/**
 * Scan-loader hand-off — the cover fades, and that is the whole story.
 *
 * The loader (`#vera-scan-loader`, see packages/ui/scan-loader/) is a full-viewport
 * white cover painted by the entry document before this bundle exists. When the app
 * resolves, it fades out over ~200ms and is removed.
 *
 * ── Why this is not a shared-element transition any more ─────────────────────────
 * It used to be. The 48px circle flew across the screen and expanded into the
 * certificate's authenticity badge, so the thing that was verifying visibly became the
 * thing that says "verified". That flight was built, rejected, rebuilt as two sequential
 * gestures (travel, then unfold), and rejected again: it read as a stretching lozenge
 * with a glyph drifting inside it. The "same object" story only pays for itself if the
 * motion is excellent — a mediocre flight is worse than no flight, because it draws the
 * eye to the one moment the product is asking to be trusted. So it is deleted, not
 * fixed a third time.
 *
 * What replaces it is two INDEPENDENT pieces with no coupling:
 *
 *   1. this module fades the cover out, and
 *   2. `AuthenticChip` animates its own entrance, in place, on mount.
 *
 * The decoupling is the point. The flight needed the cover held open for a bounded
 * window (the badge does not exist yet when the app resolves — measured 112–140ms later
 * warm, 263ms cold), a proxy `<div>` built to imitate the loader's circle, the badge's
 * geometry measured across a shadow-DOM boundary, the real badge pinned at opacity 0 and
 * an unconditional restore for it, and a ref callback threaded from the certificate page
 * through the chip's props into module state. None of that exists now. Nothing outside
 * this module is written to, so removing the cover is the only thing that has to be
 * guaranteed — which is exactly the shape the 8s failsafe already assumes.
 */

const LOADER_ID = 'vera-scan-loader';

/**
 * The pre-rename overlay, still baked into the Shopify page body of every merchant
 * installed before it (that HTML is written once at install time and never re-rendered).
 * It is not ours to style, so its presence means an immediate removal rather than a fade.
 */
const LEGACY_OVERLAY_ID = 'vera-widget-preload-overlay';

/**
 * Long enough to read as a dissolve rather than a cut, short enough that it is never
 * something the user waits through. The page underneath is already painted by this point.
 */
const FADE_MS = 200;

/**
 * Slack on the removal timer, so the node is not yanked out from under the last frame of
 * its own fade on a busy main thread. It is padding on a guarantee, not a duration.
 */
const REMOVE_GRACE_MS = 60;

/** Idempotence: `dismissScanLoader` is the terminal state and can be called repeatedly. */
let dismissed = false;

const byId = (id: string) => document.getElementById(id);

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Remove the cover. Every path through the app lands here — there is only one now.
 *
 * The fade is a nicety; the REMOVAL is not, so removal is owned by a `setTimeout` armed
 * in the same statement that starts the fade, never by the animation's `onfinish`. A
 * backgrounded tab freezes the document timeline, so `onfinish` may never fire while
 * `setTimeout` still will — that difference is the difference between a cover that
 * clears and a page bricked behind a white sheet. (The 8s CSS failsafe in the inlined
 * snippet is the separate, JS-free guarantee for the case this function never runs at
 * all. Nothing here is in its path.)
 */
export function dismissScanLoader(): void {
  if (dismissed) return;
  dismissed = true;

  // Not ours to fade, and it has no cosmetic stylesheet of its own to fade with.
  const legacy = byId(LEGACY_OVERLAY_ID);
  legacy?.remove();

  const loader = byId(LOADER_ID);
  if (!loader) return;

  if (legacy || prefersReducedMotion() || typeof loader.animate !== 'function') {
    loader.remove();
    return;
  }

  // The page behind is already painted; stop the dying cover from eating taps on it.
  loader.style.pointerEvents = 'none';

  const removal = setTimeout(() => loader.remove(), FADE_MS + REMOVE_GRACE_MS);

  try {
    loader.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: FADE_MS,
      easing: 'ease-out',
      fill: 'forwards',
    });
  } catch {
    // A WAAPI that exists but throws (older Safari on some keyframe shapes) must not
    // leave the cover sitting at opacity 1 for the length of the timer.
    clearTimeout(removal);
    loader.remove();
  }
}

/**
 * The loader's copy (VRG-529, rewritten after VRG-534 landed).
 *
 * ── WHY THIS SAYS NOTHING ABOUT VERIFICATION ─────────────────────────────────────
 *
 * By the time this cover paints, the verification is already OVER. `GET /verify/:id`
 * does the AES/CMAC check, writes the scan row and 302s — all server-side, before the
 * browser has a single byte of the brand's document. VRG-534's interstitial
 * (`apps/api/src/infrastructure/http/scan-interstitial.page.ts`) is what fills that
 * window, and it legitimately says "Verifying authenticity…" because there the work
 * genuinely has not happened yet. This cover is on the far side of the redirect, so a
 * "Verifying…" here is false, and it duplicates a claim the user has already read.
 *
 * "Verifying OWNERSHIP" was false twice over. Nothing on the scan path verifies an
 * owner at all: `verify.public.controller.ts` calls `tagRepository.getOwnership()` in
 * three places (:446, :700, :745), all of them error/retired/held branches, and it
 * resolves the COMPANY whose stock the chip is, never a consumer owner.
 *
 * ── WHAT IT IS ACTUALLY DOING, WHICH IS WHAT IT NOW SAYS ─────────────────────────
 *
 * The cover spans bundle evaluation, then `checkAuthAction` (a localStorage read;
 * on the Shopify path a token exchange), then the `useGetCompanyStyles` request that
 * resolves the brand — `App`'s dismissal effect waits for the last of those. All
 * three are plumbing. None of them is a phase a consumer can name, which is the
 * argument for ONE label rather than a progress commentary on internals.
 *
 * The label names the DESTINATION instead, and that is safe to assert because of an
 * invariant in the controller: `vt` is appended in exactly one place
 * (`verify.public.controller.ts:1097`), on the success redirect, which always also
 * carries `certificate_id`. Every failure redirect — `/tag-replaced`,
 * `/tag-not-active`, `/certificate-not-active`, and `/something-went-wrong` — carries
 * no `vt`. So `vt` present ⟹ verification succeeded ⟹ a certificate is what loads.
 *
 * `/something-went-wrong` is the newest of those and the only one NOT built in the
 * controller: `ApplicationExceptionFilter` emits it for any unhandled error on a browser
 * GET, appending `?ref=<id>` and nothing else. Listed here because this comment is what a
 * future reader will trust when deciding whether the invariant still holds, and an
 * enumeration that silently goes stale is worse than none — the invariant survives
 * precisely because that redirect carries `ref` and not `vt`.
 * Naming it is the one thing this loader knows that the snippet's default does not,
 * and after a tap that has just bounced through an interstitial onto a brand domain
 * the user may not recognise, saying where they are going is the whole value.
 *
 * ── ONE STAGE, NOT TWO — a decision, not a deletion ──────────────────────────────
 *
 * The previous version split the wait at the auth boundary: "Authenticating…" then a
 * second label. Collapsed back to one, for three reasons:
 *
 *   - "Authenticating…" is wrong on the DOMINANT path. An anonymous NFC tap has no
 *     stored session; the check looks, finds nothing, and moves on. Labelling that
 *     as authentication names a user-account operation that is not happening, and to
 *     a consumer it implies they are being logged in.
 *   - In practice only one label is ever seen anyway. loader.css holds the label at
 *     opacity 0 until 1200ms, and the auth stage is a synchronous localStorage read,
 *     so the swap has almost always already happened before anything is visible.
 *     The split bought a transition nobody watches.
 *   - A single label has to be true on every path that reaches it. Two labels made
 *     the copy correct on one path by making it wrong on the other.
 *
 * The label MECHANISM is kept — the `vt` upgrade still fires. What is gone is the
 * second stage.
 *
 * ── VRG-825: the upgraded label is "Verifying…", not "Loading the certificate…" ──
 *
 * This fires ONLY behind the `vt` gate, and that gate is what makes the word
 * honest. A `vt` is a scan token the server cryptographically verifies, so on every
 * path that reaches this constant — the anonymous tap and the Shopify
 * token-exchange boot alike — verification is the operation actually in progress.
 * It is also the more useful thing to say: "loading" describes our plumbing, while
 * "verifying" names the thing the consumer tapped the chip to find out.
 *
 * ⚠️ DO NOT PROMOTE THIS INTO THE SNIPPET'S BAKED-IN DEFAULT. That default
 * ("Loading…" in index.html) also fronts plain app boots and shared-link opens,
 * where no scan token exists and nothing is being verified. Claiming verification
 * there would be a provenance product asserting a check it never ran, and the page
 * would then resolve to a chip reading "Not verified" — the assertion contradicted
 * a second later by our own UI. The neutral default is load-bearing, not laziness;
 * it is also what the user correctly reads if the bundle never boots at all.
 */
const LABEL_VERIFYING = 'Verifying…';

/**
 * Upgrade the cover's copy from the snippet's neutral default.
 *
 * Gated on `vt`. The snippet bakes in "Loading…", which is the only thing that is
 * true before any JS has looked at the URL: this cover also fronts plain app boots
 * and shared-link opens, where no scan happened and no certificate is necessarily
 * the destination. With a `vt` there is one, per the invariant above — and if the
 * bundle never runs at all, the honest neutral default is what the user reads.
 *
 * Called from the entry module rather than from a component, because the label is
 * invisible until 1200ms and a write at bundle-eval time therefore lands long
 * before it could be seen. Purely a DOM write against the node the snippet paints;
 * no timer is involved anywhere in this file.
 */
export function upgradeScanLoaderLabel(): void {
  try {
    if (!new URL(window.location.href).searchParams.has('vt')) return;
  } catch {
    return;
  }

  const loader = byId(LOADER_ID);
  const label = loader?.querySelector<HTMLElement>('.vera-scan-loader__label');

  if (!loader || !label) return;

  label.textContent = LABEL_VERIFYING;
  // role="status", so this is the accessible name a screen reader announces. Leaving it
  // at the snippet's default would make the two halves disagree about what is happening.
  //
  // VRG-825 — it MUST track `LABEL_VERIFYING`. These are two renderings of one state,
  // and nothing in the type system ties them together: changing the visible string
  // alone leaves a sighted user reading "Verifying…" while a screen reader announces
  // "Loading the certificate", which is the exact disagreement the line above exists
  // to prevent. No ellipsis here — a screen reader reads the character aloud.
  loader.setAttribute('aria-label', LABEL_VERIFYING.replace('…', ''));
}
